import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Plus, Pencil, Trash2, Loader2, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useToast } from '@/lib/hooks/use-toast';
import { ServiceActivity, CreateServiceActivityData } from '../types/material.types';
import { materialService } from '../services/material-service';

interface ServiceActivitiesProps {
  materialId: string;
  activities: ServiceActivity[];
  onActivitiesChange: (activities: ServiceActivity[]) => void;
}

interface ActivityFormData {
  name: string;
  description: string;
  estimatedHours: number;
  hourlyRate: number;
  isOptional: boolean;
  sortOrder: number;
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('es-CO', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

export function ServiceActivities({
  materialId,
  activities,
  onActivitiesChange,
}: ServiceActivitiesProps) {
  const { toast } = useToast();
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<ActivityFormData>({
    defaultValues: {
      name: '',
      description: '',
      estimatedHours: 1,
      hourlyRate: 0,
      isOptional: false,
      sortOrder: activities.length + 1,
    },
  });

  const isOptional = watch('isOptional');

  const requiredCost = activities
    .filter((a) => !a.isOptional)
    .reduce((sum, a) => sum + a.estimatedHours * a.hourlyRate, 0);

  const totalCost = activities.reduce((sum, a) => sum + a.estimatedHours * a.hourlyRate, 0);

  const openCreate = () => {
    reset({
      name: '',
      description: '',
      estimatedHours: 1,
      hourlyRate: 0,
      isOptional: false,
      sortOrder: activities.length + 1,
    });
    setEditingId(null);
    setShowForm(true);
  };

  const openEdit = (activity: ServiceActivity) => {
    reset({
      name: activity.name,
      description: activity.description || '',
      estimatedHours: activity.estimatedHours,
      hourlyRate: activity.hourlyRate,
      isOptional: activity.isOptional,
      sortOrder: activity.sortOrder,
    });
    setEditingId(activity.id);
    setShowForm(true);
  };

  const cancelForm = () => {
    setShowForm(false);
    setEditingId(null);
    reset();
  };

  const onSubmit = async (data: ActivityFormData) => {
    const payload: CreateServiceActivityData = {
      name: data.name,
      description: data.description || null,
      estimatedHours: data.estimatedHours,
      hourlyRate: data.hourlyRate,
      isOptional: data.isOptional,
      sortOrder: data.sortOrder,
    };

    try {
      setIsSubmitting(true);
      if (editingId) {
        const updated = await materialService.updateActivity(materialId, editingId, payload);
        onActivitiesChange(activities.map((a) => (a.id === editingId ? updated : a)));
        toast({ title: 'Actividad actualizada', variant: 'success' });
      } else {
        const created = await materialService.createActivity(materialId, payload);
        onActivitiesChange([...activities, created]);
        toast({ title: 'Actividad creada', variant: 'success' });
      }
      cancelForm();
    } catch {
      toast({ title: 'Error al guardar la actividad', variant: 'destructive' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (activityId: string) => {
    try {
      setDeletingId(activityId);
      await materialService.deleteActivity(materialId, activityId);
      onActivitiesChange(activities.filter((a) => a.id !== activityId));
      toast({ title: 'Actividad eliminada', variant: 'success' });
    } catch {
      toast({ title: 'Error al eliminar la actividad', variant: 'destructive' });
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
          Actividades del Servicio
        </p>
        {!showForm && (
          <Button type="button" variant="outline" size="sm" onClick={openCreate}>
            <Plus className="h-4 w-4 mr-1" />
            Agregar actividad
          </Button>
        )}
      </div>

      {/* Formulario inline */}
      {showForm && (
        <div className="border border-border rounded-lg p-4 space-y-4 bg-muted/20">
          <p className="text-sm font-semibold">
            {editingId ? 'Editar actividad' : 'Nueva actividad'}
          </p>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="act-name">Nombre *</Label>
                <Input
                  id="act-name"
                  placeholder="Nombre de la actividad"
                  {...register('name', { required: 'Nombre requerido' })}
                  disabled={isSubmitting}
                />
                {errors.name && <p className="text-xs text-destructive">{errors.name.message}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="act-sortOrder">Orden</Label>
                <Input
                  id="act-sortOrder"
                  type="number"
                  min="1"
                  {...register('sortOrder', { valueAsNumber: true })}
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="act-description">Descripción</Label>
              <Textarea
                id="act-description"
                rows={2}
                placeholder="Descripción de la actividad"
                {...register('description')}
                disabled={isSubmitting}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="act-hours">Horas estimadas *</Label>
                <Input
                  id="act-hours"
                  type="number"
                  step="0.5"
                  min="0.5"
                  placeholder="1"
                  {...register('estimatedHours', {
                    valueAsNumber: true,
                    required: 'Requerido',
                    min: { value: 0.5, message: 'Mínimo 0.5 horas' },
                  })}
                  disabled={isSubmitting}
                />
                {errors.estimatedHours && (
                  <p className="text-xs text-destructive">{errors.estimatedHours.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="act-rate">Tarifa / hora *</Label>
                <Input
                  id="act-rate"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  {...register('hourlyRate', {
                    valueAsNumber: true,
                    required: 'Requerido',
                    min: { value: 0, message: 'Mínimo 0' },
                  })}
                  disabled={isSubmitting}
                />
                {errors.hourlyRate && (
                  <p className="text-xs text-destructive">{errors.hourlyRate.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label>Opcional</Label>
                <div className="flex items-center gap-2 pt-2">
                  <Switch
                    checked={isOptional}
                    onCheckedChange={(checked) => setValue('isOptional', checked)}
                    disabled={isSubmitting}
                  />
                  <Label className="cursor-pointer text-sm font-normal">
                    {isOptional ? 'Si' : 'No'}
                  </Label>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={cancelForm}
                disabled={isSubmitting}
              >
                <X className="h-4 w-4 mr-1" />
                Cancelar
              </Button>
              <Button type="submit" size="sm" disabled={isSubmitting}>
                {isSubmitting ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-1" />
                ) : (
                  <Check className="h-4 w-4 mr-1" />
                )}
                {editingId ? 'Actualizar' : 'Agregar'}
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Tabla de actividades */}
      {activities.length > 0 ? (
        <div className="border border-border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>#</TableHead>
                <TableHead>Actividad</TableHead>
                <TableHead className="text-right">Horas</TableHead>
                <TableHead className="text-right">Tarifa/hr</TableHead>
                <TableHead className="text-right">Subtotal</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {activities
                .sort((a, b) => a.sortOrder - b.sortOrder)
                .map((activity) => (
                  <TableRow key={activity.id}>
                    <TableCell className="text-muted-foreground text-sm">
                      {activity.sortOrder}
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium text-sm">{activity.name}</p>
                        {activity.description && (
                          <p className="text-xs text-muted-foreground">{activity.description}</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right text-sm">{activity.estimatedHours}</TableCell>
                    <TableCell className="text-right text-sm">
                      {formatCurrency(activity.hourlyRate)}
                    </TableCell>
                    <TableCell className="text-right text-sm font-medium">
                      {formatCurrency(activity.estimatedHours * activity.hourlyRate)}
                    </TableCell>
                    <TableCell>
                      {activity.isOptional ? (
                        <Badge variant="secondary">Opcional</Badge>
                      ) : (
                        <Badge variant="default">Requerido</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => openEdit(activity)}
                          disabled={!!deletingId}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(activity.id)}
                          disabled={deletingId === activity.id}
                          className="text-destructive hover:text-destructive"
                        >
                          {deletingId === activity.id ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <Trash2 className="h-3.5 w-3.5" />
                          )}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </div>
      ) : (
        !showForm && (
          <p className="text-sm text-muted-foreground text-center py-4">
            No hay actividades registradas. Agregue la primera actividad del servicio.
          </p>
        )
      )}

      {/* Resumen de costos */}
      {activities.length > 0 && (
        <div className="flex justify-end gap-6 text-sm border-t border-border pt-3">
          <div className="text-right">
            <p className="text-muted-foreground">Costo requerido</p>
            <p className="font-semibold">{formatCurrency(requiredCost)}</p>
          </div>
          {totalCost !== requiredCost && (
            <div className="text-right">
              <p className="text-muted-foreground">Costo total (con opcionales)</p>
              <p className="font-semibold">{formatCurrency(totalCost)}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default ServiceActivities;
